import React, { useState, useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Container,
  Grid,
  alpha,
Snackbar,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { SEARCE_COLORS } from '../../../theme';
import {
  customerRequestApi,
  FeedbackRequestCreate,
  EscalationRequestCreate,
  AppreciationRequestCreate,
  SolutionStoryDraftUpsert,
  SolutionStorySubmitBody,
  SolutionStoryResponse,
  PublicTestimonialRequestCreate,
  ProjectDropdownOption,
  TeamMemberOption,
} from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { FeedbackForm, type FeedbackFormHandle } from './FeedbackForm';
import { EscalationForm } from './EscalationForm';
import { AppreciationForm } from './AppreciationForm';
import { SolutionStoryForm } from './SolutionStoryForm';
import { PublicTestimonialForm } from './PublicTestimonialForm';
import { ActionButtons } from './ActionButtons';
import { SubmissionStatusModal } from './SubmissionStatusModal';
import { RequestTypeSelectionPage } from './RequestTypeSelectionPage';
import { RequestType, LevelType, FormData, initialFormData, SubmissionError } from './types';
import { getFormTitle, getLevelLabel, validateForm } from './utils';
import { toBackendEmailList } from './emailRecipients';
import { getApiErrorMessage } from '../../../utils/apiErrorUtils';

// Page Header Component (for form page only)
interface PageHeaderProps {
  title: string;
  subtitle?: string;
  actionButton?: React.ReactNode;
}

const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, actionButton }) => (
  <Box
    sx={{
      backgroundColor: SEARCE_COLORS.white,
      borderBottom: `1px solid ${SEARCE_COLORS.neutral_200}`,
      px: 4,
      py: 1.5,
      position: 'sticky',
      top: 0,
      zIndex: 1200,
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
      mx: { xs: -2, sm: -3, md: -4 },
      mb: 3,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <Box>
      <Typography
        variant="h4"
        component="h1"
        sx={{
          fontWeight: 700,
          color: SEARCE_COLORS.textPrimary,
          fontSize: { xs: '1.3rem', sm: '1.5rem', md: '1.75rem' },
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
          background: `linear-gradient(135deg, ${SEARCE_COLORS.primary} 0%, ${SEARCE_COLORS.secondary} 100%)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text',
          mb: subtitle ? 0.5 : 0,
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          variant="body2"
          component="div"
          sx={{
            color: SEARCE_COLORS.textSecondary,
            fontWeight: 350,
            fontSize: '0.875rem',
          }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
    {actionButton && <Box>{actionButton}</Box>}
  </Box>
);

/** Ensure 4 slots for solution story array fields */
function padSolutionStoryStrings(arr?: string[] | null): string[] {
  const a = [...(arr || [])];
  while (a.length < 4) a.push('');
  return a.slice(0, 4);
}

/** Platform is stored as a comma-separated string (like tech_stack in the catalog table). */
function parsePlatformList(value?: string | null): string[] {
  if (!value) return [];
  return value.split(',').map((v) => v.trim()).filter(Boolean);
}

function normalizeReferencable(value?: string | null): string {
  const v = (value || '').trim();
  if (v === 'R' || v === 'NR') return v;
  if (v.toLowerCase() === 'referencable' || v.toLowerCase() === 'referenceable') return 'R';
  if (v.toLowerCase() === 'non-referencable' || v.toLowerCase() === 'non-referenceable') return 'NR';
  return '';
}

function mapSolutionStoryResponseToFormData(story: SolutionStoryResponse): Partial<FormData> {
  const ts = story.tech_stack && story.tech_stack.length > 0 ? story.tech_stack : [''];
  return {
    businessUnit: story.business_unit || '',
    projectCode: story.project_code || '',
    accountName: story.account_name || '',
    opportunity: story.opportunity || '',
    opportunityProjectOwner: story.project_owner || '',
    opportunityProjectOwnerEmail: story.project_owner_email || '',
    workload: story.workload || '',
    deliverySubBu: story.sub_bu || '',
    solutionProvided: story.solution_provided || '',
    keyObjectivesChallenges: story.key_objectives_challenges || '',
    executiveSummary: story.executive_summary || '',
    challengesChangedSinceInception: story.challenges_changed_since_inception || '',
    solutionDescription: story.solution_description || '',
    practice: story.practice || '',
    platform: parsePlatformList(story.platform),
    platformUsageWay: story.platform_usage_way || '',
    businessContext: padSolutionStoryStrings(story.business_context),
    solution: padSolutionStoryStrings(story.solution),
    impact: padSolutionStoryStrings(story.impact),
    detailedSolutionImpact: story.detailed_solution_impact || '',
    techStack: ts,
    techUsageWay: story.tech_usage_way || '',
    referencable: normalizeReferencable(story.referencable),
    industryGroup: story.industry_group || '',
    industry: story.industry || '',
    teamworkCollaboration: story.teamwork_collaboration || '',
    resultsBusinessImpact: story.results_business_impact || '',
    solutionStoryFiles: [],
  };
}

export default function CustomerFeedbackFormPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const feedbackFormRef = useRef<FeedbackFormHandle | null>(null);
  const [selectedRequestType, setSelectedRequestType] = useState<RequestType>(null);
  const [selectedLevel, setSelectedLevel] = useState<LevelType>(null);
  const [isEscalationResolved, setIsEscalationResolved] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const [loading, setLoading] = useState(false);
  const [fileUploading, setFileUploading] = useState(false);
  const [slideCreating, setSlideCreating] = useState(false);
  const [solutionStoryDraftId, setSolutionStoryDraftId] = useState<string | null>(null);
  const [solutionStoryExistingFileUrls, setSolutionStoryExistingFileUrls] = useState<string[]>([]);
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftStoryProjectCode, setDraftStoryProjectCode] = useState<string | null>(null);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Error state for real-time feedback in modal
  const [submissionError, setSubmissionError] = useState<SubmissionError>({ stage: null, message: '', details: '' });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  // Project dropdown state for Appreciation form
  const [projectOptions, setProjectOptions] = useState<ProjectDropdownOption[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectDropdownOption | null>(null);
  // Filter by project status for project dropdown (null = All, default Closed)
  const [projectStatusFilter, setProjectStatusFilter] = useState<string | null>('Closed');

  // Team member multi-select state
  const [teamMemberOptions, setTeamMemberOptions] = useState<TeamMemberOption[]>([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<TeamMemberOption[]>([]);

  // Set user email in formData when form is shown
  useEffect(() => {
    if (user?.email && showForm) {
      setFormData(prev => ({ ...prev, email: user.email }));
    }
  }, [user?.email, showForm]);

  // Fetch user's projects when showing any form that needs project dropdown
  useEffect(() => {
    if ((selectedRequestType === 'appreciation' || selectedRequestType === 'escalation' || selectedRequestType === 'feedback' || selectedRequestType === 'solution-story' || selectedRequestType === 'public-testimonial') && showForm) {
      const statusParam =
        projectStatusFilter == null || projectStatusFilter === 'All'
          ? undefined
          : projectStatusFilter;
      fetchUserProjects(statusParam);
    }
  }, [selectedRequestType, showForm, projectStatusFilter]);

  const fetchUserProjects = async (statusFilter?: string) => {
    setProjectsLoading(true);
    try {
      const response = await customerRequestApi.getUserProjects(statusFilter);
      setProjectOptions(response.projects || []);
    } catch (error) {
      console.error('Failed to fetch user projects:', error);
      showSnackbar('Failed to load projects. Please try again.', 'error');
    } finally {
      setProjectsLoading(false);
    }
  };

  // Fetch team members when a project is selected
  const fetchTeamMembers = async (projectCode: string) => {
    setTeamMembersLoading(true);
    setTeamMemberOptions([]);
    setSelectedTeamMembers([]);
    try {
      const response = await customerRequestApi.getProjectTeamMembers(projectCode);
      setTeamMemberOptions(response.team_members || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      showSnackbar('Failed to load team members. Please try again.', 'error');
    } finally {
      setTeamMembersLoading(false);
    }
  };


  // Handle project selection - auto-fill related fields and fetch team members
  const handleProjectSelect = (project: ProjectDropdownOption | null) => {
    setSelectedProject(project);
    // Reset team members when project changes
    setSelectedTeamMembers([]);
    setTeamMemberOptions([]);
    
    if (project) {
      setFormData(prev => ({
        ...prev,
        projectCode: project.project_code,
        projectOwnerName: project.owner || '',
        accountName: project.account || '',
        businessUnit: project.delivery_bu || '',
        opportunity: project.opportunity_name || '-',  // Auto-fill opportunity from BQ
        opportunityProjectOwner: project.owner || '',
        opportunityProjectOwnerEmail: project.project_owner_email || '',
        workload: project.workload || '', // Auto-fill workload for Solution Story
        deliverySubBu: project.delivery_sub_bu || '', // Auto-fill Delivery Sub BU for Solution Story
        projectMemberName: '', // Reset project member when project changes
      }));
      // Fetch team members for the selected project (for individual level forms)
      if (selectedLevel === 'individual') {
        fetchTeamMembers(project.project_code);
      }
    } else {
      setFormData(prev => ({
        ...prev,
        projectCode: '',
        projectOwnerName: '',
        accountName: '',
        businessUnit: '',
        opportunity: '',  // Reset opportunity when project is cleared
        opportunityProjectOwner: '',
        opportunityProjectOwnerEmail: '',
        workload: '', // Reset workload when project is cleared
        deliverySubBu: '', // Reset Delivery Sub BU when project is cleared
        projectMemberName: '',
      }));
    }
  };

  // Handle team member selection - store as comma-separated emails
  const handleTeamMemberSelect = (members: TeamMemberOption[]) => {
    setSelectedTeamMembers(members);
    const emailsString = members.map(m => m.email).join(', ');
    setFormData(prev => ({
      ...prev,
      projectMemberName: emailsString,
    }));
  };

  // Check if form is in a disabled state (loading, success, or error modal open)
  const isFormDisabled = loading || submissionSuccess || submissionError.stage !== null;


  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Deep link: /customer-feedback?type=solution-story&draftId=...
  useEffect(() => {
    const t = searchParams.get('type');
    const d = searchParams.get('draftId');
    if (t === 'solution-story' && d) {
      setSelectedRequestType('solution-story');
      setShowForm(true);
    }
  }, [searchParams]);

  const draftIdParam = searchParams.get('draftId');
  useEffect(() => {
    if (!draftIdParam || selectedRequestType !== 'solution-story' || !showForm) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const story = await customerRequestApi.getSolutionStoryById(draftIdParam);
        if (cancelled) return;
        if (!['Draft', 'Failed'].includes(story.status)) {
          showSnackbar('This story cannot be edited here.', 'error');
          setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            next.delete('draftId');
            return next;
          }, { replace: true });
          return;
        }
        setSolutionStoryDraftId(story.id);
        setSolutionStoryExistingFileUrls(story.file_urls || []);
        setDraftStoryProjectCode(story.project_code || null);
        setFormData((prev) => ({
          ...prev,
          ...mapSolutionStoryResponseToFormData(story),
        }));
      } catch (err) {
        console.error(err);
        showSnackbar(getApiErrorMessage(err as Error, 'Failed to load draft'), 'error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draftIdParam, selectedRequestType, showForm]);

  useEffect(() => {
    if (!draftStoryProjectCode || !projectOptions.length) return;
    const p = projectOptions.find((o) => o.project_code === draftStoryProjectCode);
    if (p) setSelectedProject(p);
  }, [draftStoryProjectCode, projectOptions]);

  const handleRequestTypeSelect = (type: RequestType) => {
    setSelectedRequestType(type);
    if (type === 'escalation') {
      setIsEscalationResolved(false);
    }
    // Customer Feedback and Solution Story directly open the form without level selection
    if (type === 'feedback' || type === 'solution-story' || type === 'public-testimonial') {
      setSelectedLevel(null); // No level needed for feedback, solution story, or public testimonial
      setShowForm(true);
    }
  };

  const handleLevelSelect = (level: LevelType) => {
    setSelectedLevel(level);
    if (selectedRequestType === 'escalation') {
      setIsEscalationResolved(false);
      setFormData((prev) => ({
        ...prev,
        escalatedBySource: '',
        escalatedByName: '',
        escalatedByDesignation: '',
      }));
    }
    setShowForm(true);
  };

  const handleBackToAnalytics = () => {
    navigate('/customer-sentiment');
  };

  const handleBackToRequestSelection = () => {
    // Reset all form states
    setSelectedRequestType(null);
    setSelectedLevel(null);
    setIsEscalationResolved(false);
    setShowForm(false);
    setFormData(initialFormData);
    setSolutionStoryDraftId(null);
    setSolutionStoryExistingFileUrls([]);
    setDraftStoryProjectCode(null);
    setSearchParams({}, { replace: true });
    // Reset project dropdown state
    setSelectedProject(null);
    setProjectOptions([]);
    // Reset team member dropdown state
    setSelectedTeamMembers([]);
    setTeamMemberOptions([]);
    // Reset submission states
    setLoading(false);
    setFileUploading(false);
    setSlideCreating(false);
    setSubmissionSuccess(false);
    setSuccessMessage('');
    // Close any open snackbar
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleInputChange = (field: keyof FormData, value: string | number | null | File | File[] | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const uploadPendingSolutionStoryFiles = async (): Promise<string[]> => {
    const urls: string[] = [];
    for (const file of formData.solutionStoryFiles || []) {
      const uploadResult = await customerRequestApi.uploadFile(file, 'solution-story');
      if (uploadResult.file_url) urls.push(uploadResult.file_url);
    }
    return urls;
  };

  const mergeSolutionStoryFileUrls = (newUrls: string[]) => {
    const merged = [...solutionStoryExistingFileUrls];
    for (const u of newUrls) {
      if (u && !merged.includes(u)) merged.push(u);
    }
    return merged;
  };

  const handleSaveSolutionStoryDraft = async () => {
    setDraftSaving(true);
    try {
      if (formData.solutionStoryFiles?.length) {
        setFileUploading(true);
      }
      const newUrls = await uploadPendingSolutionStoryFiles();
      setFileUploading(false);
      const merged = mergeSolutionStoryFileUrls(newUrls);
      const payload: SolutionStoryDraftUpsert = {
        request_type: 'solution-story',
        id: solutionStoryDraftId || undefined,
        business_unit: formData.businessUnit || undefined,
        project_code: formData.projectCode || undefined,
        project_name: selectedProject?.project_name || undefined,
        account_name: formData.accountName || undefined,
        opportunity: formData.opportunity || undefined,
        project_owner: formData.opportunityProjectOwner || undefined,
        project_owner_email: formData.opportunityProjectOwnerEmail || undefined,
        sub_bu: formData.deliverySubBu || undefined,
        workload: formData.workload || undefined,
        solution_provided: formData.solutionProvided || undefined,
        key_objectives_challenges: formData.keyObjectivesChallenges || undefined,
        executive_summary: formData.executiveSummary || undefined,
        challenges_changed_since_inception: formData.challengesChangedSinceInception || undefined,
        solution_description: formData.solutionDescription || undefined,
        practice: formData.practice || undefined,
        platform: formData.platform && formData.platform.length > 0 ? formData.platform.join(', ') : undefined,
        platform_usage_way: formData.platformUsageWay || undefined,
        business_context: formData.businessContext || undefined,
        solution: formData.solution || undefined,
        impact: formData.impact || undefined,
        detailed_solution_impact: formData.detailedSolutionImpact || undefined,
        tech_stack: formData.techStack?.some((t) => t.trim()) ? formData.techStack : undefined,
        tech_usage_way: formData.techUsageWay || undefined,
        referencable: formData.referencable || undefined,
        industry_group: formData.industryGroup || undefined,
        industry: formData.industry || undefined,
        teamwork_collaboration: formData.teamworkCollaboration || undefined,
        results_business_impact: formData.resultsBusinessImpact || undefined,
        file_urls: merged.length ? merged : undefined,
      };
      const res = await customerRequestApi.saveSolutionStoryDraft(payload);
      setSolutionStoryDraftId(res.id);
      setSolutionStoryExistingFileUrls(res.file_urls || merged);
      handleInputChange('solutionStoryFiles', []);
      setSearchParams({ type: 'solution-story', draftId: res.id }, { replace: true });
      showSnackbar('Draft saved', 'success');
    } catch (e) {
      showSnackbar(getApiErrorMessage(e as Error, 'Failed to save draft'), 'error');
    } finally {
      setDraftSaving(false);
      setFileUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedRequestType === 'solution-story') {
      // Handle multiple files for solution story
      const files = event.target.files ? Array.from(event.target.files) : [];
      const currentFiles = formData.solutionStoryFiles || [];
      // Merge with existing files (avoid duplicates by name)
      const existingFileNames = new Set(currentFiles.map(f => f.name));
      const newFiles = files.filter(f => !existingFileNames.has(f.name));
      handleInputChange('solutionStoryFiles', [...currentFiles, ...newFiles]);
    } else if (selectedRequestType === 'public-testimonial') {
      // Handle single file for public testimonial
      const file = event.target.files?.[0] || null;
      handleInputChange('publicTestimonialFile', file);
    } else {
      // Handle single file for other forms
      const file = event.target.files?.[0] || null;
      handleInputChange('closureReport', file);
    }
  };


  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedRequestType === 'feedback') {
      flushSync(() => {
        feedbackFormRef.current?.flushRecipientInputs();
      });
    }

    // Validate form (use ref so post-flush recipient emails are included after flushSync)
    const validationError = validateForm(
      selectedRequestType,
      formDataRef.current,
      selectedLevel,
      isEscalationResolved
    );
    if (validationError) {
      showSnackbar(validationError, 'error');
      return;
    }

    setLoading(true);
    setSubmissionError({ stage: null, message: '', details: '', ticketId: '' }); // Clear any previous errors

    try {
      let requestNumber = '';
      let successMsg = '';

      if (selectedRequestType === 'feedback') {
        const fd = formDataRef.current;
        // STAGE 1: Upload closure report file
        let closureReportUrl: string | undefined = undefined;
        
        if (fd.closureReport) {
          try {
            setFileUploading(true);
            const uploadResult = await customerRequestApi.uploadFile(
              fd.closureReport,
              'feedback'
            );
            closureReportUrl = uploadResult.file_url;
            setFileUploading(false);
          } catch (uploadError: any) {
            setFileUploading(false);
            console.error('File upload error:', uploadError);
            const errorMsg = getApiErrorMessage(uploadError, 'Failed to upload closure report');
            const errorDetails = uploadError.response?.data?.message || uploadError.message || 'Please check your file and try again';
            setSubmissionError({
              stage: 'upload',
              message: errorMsg,
              details: errorDetails,
            });
            return; // Keep modal open with error
          }
        }

        // STAGE 2: Create Feedback Request
        try {
          const feedbackData: FeedbackRequestCreate = {
            request_type: 'feedback',
            to_emails: toBackendEmailList(fd.toEmails),
            project_title: fd.projectTitle || undefined,
            customer_contact_name: fd.customerContactName || undefined,
            cc_emails: toBackendEmailList(fd.ccEmails) || undefined,
            business_unit: fd.businessUnit || undefined,
            project_code: fd.projectCode || undefined,
            project_name: selectedProject?.project_name || undefined,
            account_name: fd.accountName || undefined,
            opportunity: fd.opportunity || undefined,
            project_owner: fd.opportunityProjectOwner || undefined,
            closure_report_url: closureReportUrl,
          };

          const response = await customerRequestApi.createFeedback(feedbackData);
          requestNumber = response.request_number;
          
          // Check if email was sent successfully (backend returns email_error if failed)
          if (response.feedback_detail?.email_error) {
            setSubmissionError({
              stage: 'email',
              message: 'Request created but email delivery failed',
              details: response.feedback_detail.email_error,
              ticketId: requestNumber, // Include ticket ID for tracking
            });
            return;
          }
          
          successMsg = `Feedback request ${requestNumber} created successfully!`;
        } catch (submitError: any) {
          console.error('Submission error:', submitError);
          const errorMsg = getApiErrorMessage(submitError, 'Failed to create feedback request');
          const errorDetails = submitError.response?.data?.message || submitError.message || 'Database or server error occurred';
          
          // Check if it's an email-related error
          if (errorMsg.toLowerCase().includes('email') || errorMsg.toLowerCase().includes('smtp')) {
            setSubmissionError({
              stage: 'email',
              message: 'Failed to send email to customer',
              details: errorDetails,
            });
          } else {
            setSubmissionError({
              stage: 'submission',
              message: errorMsg,
              details: errorDetails,
            });
          }
          return;
        }
        
      } else if (selectedRequestType === 'solution-story') {
        let fileUrls: string[] = [...solutionStoryExistingFileUrls];

        if (formData.solutionStoryFiles && formData.solutionStoryFiles.length > 0) {
          try {
            setFileUploading(true);
            const newUrls = await uploadPendingSolutionStoryFiles();
            fileUrls = mergeSolutionStoryFileUrls(newUrls);
            setFileUploading(false);
          } catch (uploadError: any) {
            setFileUploading(false);
            console.error('File upload error:', uploadError);
            const errorMsg = getApiErrorMessage(uploadError, 'Failed to upload files');
            const errorDetails = uploadError.response?.data?.message || uploadError.message || 'Please check your files and try again';
            setSubmissionError({
              stage: 'upload',
              message: errorMsg,
              details: errorDetails,
            });
            return;
          }
        }

        try {
          setSlideCreating(true);
          const solutionStoryData: SolutionStorySubmitBody = {
            request_type: 'solution-story',
            solution_provided: formData.solutionProvided,
            key_objectives_challenges: formData.keyObjectivesChallenges,
            executive_summary: formData.executiveSummary,
            challenges_changed_since_inception: formData.challengesChangedSinceInception,
            solution_description: formData.solutionDescription,
            practice: formData.practice,
            platform: formData.platform && formData.platform.length > 0 ? formData.platform.join(', ') : undefined,
            platform_usage_way: formData.platformUsageWay,
            business_context: formData.businessContext || [],
            solution: formData.solution || [],
            impact: formData.impact || [],
            detailed_solution_impact: formData.detailedSolutionImpact,
            tech_stack: formData.techStack && formData.techStack.length > 0 && formData.techStack.some(v => v.trim()) ? formData.techStack : undefined,
            tech_usage_way: formData.techUsageWay,
            referencable: formData.referencable as 'R' | 'NR',
            industry_group: formData.industryGroup || undefined,
            industry: formData.industry || undefined,
            teamwork_collaboration: formData.teamworkCollaboration || undefined,
            results_business_impact: formData.resultsBusinessImpact || undefined,
            business_unit: formData.businessUnit || undefined,
            project_code: formData.projectCode || undefined,
            project_name: selectedProject?.project_name || undefined,
            account_name: formData.accountName || undefined,
            opportunity: formData.opportunity || undefined,
            project_owner: formData.opportunityProjectOwner || undefined,
            project_owner_email: formData.opportunityProjectOwnerEmail || undefined,
            workload: formData.workload || undefined,
            file_urls: fileUrls.length > 0 ? fileUrls : undefined,
            draft_id: solutionStoryDraftId || undefined,
          };

          const response = await customerRequestApi.createSolutionStory(solutionStoryData);
          requestNumber = response.request_number || response.id.substring(0, 8) + '...';
          successMsg = `Solution story request ${requestNumber} created successfully! It has been submitted for review.`;
          setSolutionStoryDraftId(null);
          setSolutionStoryExistingFileUrls(response.file_urls || []);
          setSlideCreating(false);
        } catch (submitError: any) {
          setSlideCreating(false);
          console.error('Submission error:', submitError);
          const errorMsg = getApiErrorMessage(submitError, 'Failed to create solution story request');
          const errorDetails = submitError.response?.data?.detail || submitError.response?.data?.message || submitError.message || 'Database or server error occurred';
          setSubmissionError({
            stage: 'submission',
            message: errorMsg,
            details: typeof errorDetails === 'string' ? errorDetails : JSON.stringify(errorDetails),
          });
          return;
        }
        
      } else if (selectedRequestType === 'public-testimonial') {
        // STAGE 1: Upload file if any
        let fileUrl: string | undefined = undefined;
        
        if (formData.publicTestimonialFile) {
          try {
            setFileUploading(true);
            const uploadResult = await customerRequestApi.uploadFile(
              formData.publicTestimonialFile,
              'public-testimonial'
            );
            fileUrl = uploadResult.file_url;
            setFileUploading(false);
          } catch (uploadError: any) {
            setFileUploading(false);
            console.error('File upload error:', uploadError);
            const errorMsg = getApiErrorMessage(uploadError, 'Failed to upload file');
            const errorDetails = uploadError.response?.data?.message || uploadError.message || 'Please check your file and try again';
            setSubmissionError({
              stage: 'upload',
              message: errorMsg,
              details: errorDetails,
            });
            return;
          }
        }

        // STAGE 2: Create Public Testimonial Request
        try {
          const publicTestimonialData: PublicTestimonialRequestCreate = {
            request_type: 'public-testimonial',
            business_unit: formData.businessUnit || undefined,
            project_code: formData.projectCode || undefined,
            project_name: selectedProject?.project_name || undefined,
            account_name: formData.accountName || undefined,
            opportunity: formData.opportunity || undefined,
            project_owner: formData.opportunityProjectOwner || undefined,
            sub_bu: formData.deliverySubBu || undefined,
            customer_poc_name: formData.customerPocName,
            customer_poc_designation: formData.customerPocDesignation,
            date_testimony_received: formData.dateTestimonyReceived || undefined,
            public_testimonial_link: formData.publicTestimonialLink,
            file_url: fileUrl,
          };

          const response = await customerRequestApi.createPublicTestimonial(publicTestimonialData);
          requestNumber = response.request_number;
          successMsg = `Public testimonial request ${requestNumber} created successfully!`;
        } catch (submitError: any) {
          console.error('Submission error:', submitError);
          const errorMsg = getApiErrorMessage(submitError, 'Failed to create public testimonial request');
          const errorDetails = submitError.response?.data?.message || submitError.message || 'Database or server error occurred';
          setSubmissionError({
            stage: 'submission',
            message: errorMsg,
            details: errorDetails,
          });
          return;
        }
        
      } else if (selectedRequestType === 'escalation') {
        // STAGE 2: Create Escalation Request (no file upload)
        try {
          const areaOfEscalationValue = formData.areaOfEscalation === 'Other'
            ? (formData.areaOfEscalationOther || undefined)
            : (formData.areaOfEscalation || undefined);
          const escalationData: EscalationRequestCreate = {
            request_type: 'escalation',
            level: selectedLevel as 'project' | 'individual',
            issue_description: formData.issueDescription,
            date_raised: formData.dateRaised,
            project_member_name: formData.projectMemberName || undefined,
            escalated_by_name: formData.escalatedByName || undefined,
            escalated_by_designation: formData.escalatedByDesignation || undefined,
            area_of_escalation: areaOfEscalationValue,
            date_resolved: formData.dateResolved || undefined,
            solution_proposed: formData.solutionProposed || undefined,
            root_cause: formData.rootCause || undefined,
            learnings: formData.learnings || undefined,
            project_code: formData.projectCode || undefined,
            project_name: selectedProject?.project_name || undefined,
            project_owner: formData.projectOwnerName || undefined,
          };

          const response = await customerRequestApi.createEscalation(escalationData);
          requestNumber = response.request_number;
          successMsg = `Escalation request ${requestNumber} created successfully!`;
        } catch (submitError: any) {
          console.error('Submission error:', submitError);
          const errorMsg = getApiErrorMessage(submitError, 'Failed to create escalation request');
          const errorDetails = submitError.response?.data?.message || submitError.message || 'Database or server error occurred';
          setSubmissionError({
            stage: 'submission',
            message: errorMsg,
            details: errorDetails,
          });
          return;
        }
        
      } else if (selectedRequestType === 'appreciation') {
        // STAGE 1: Upload feedback file
        let feedbackFileUrl: string | undefined = undefined;
        
        if (formData.appreciationFile) {
          try {
            setFileUploading(true);
            const uploadResult = await customerRequestApi.uploadFile(
              formData.appreciationFile,
              'appreciation'
            );
            feedbackFileUrl = uploadResult.file_url;
            setFileUploading(false);
          } catch (uploadError: any) {
            setFileUploading(false);
            console.error('File upload error:', uploadError);
            const errorMsg = getApiErrorMessage(uploadError, 'Failed to upload feedback file');
            const errorDetails = uploadError.response?.data?.message || uploadError.message || 'Please check your file and try again';
            setSubmissionError({
              stage: 'upload',
              message: errorMsg,
              details: errorDetails,
            });
            return;
          }
        }

        // STAGE 2: Create Appreciation Request
        try {
          const appreciationData: AppreciationRequestCreate = {
            request_type: 'appreciation',
            level: selectedLevel as 'project' | 'individual',
            feedback_snippet: formData.feedbackSnippet,
            project_member_name: formData.projectMemberName || undefined,
            customer_spoc_name: formData.customerSpocName || undefined,
            customer_spoc_designation: formData.customerSpocDesignation || undefined,
            project_code: formData.projectCode || undefined,
            project_name: selectedProject?.project_name || undefined,
            project_owner: formData.projectOwnerName || undefined,
            feedback_file_url: feedbackFileUrl,
          };

          const response = await customerRequestApi.createAppreciation(appreciationData);
          requestNumber = response.request_number;
          successMsg = `Appreciation request ${requestNumber} created successfully!`;
        } catch (submitError: any) {
          console.error('Submission error:', submitError);
          const errorMsg = getApiErrorMessage(submitError, 'Failed to create appreciation request');
          const errorDetails = submitError.response?.data?.message || submitError.message || 'Database or server error occurred';
          setSubmissionError({
            stage: 'submission',
            message: errorMsg,
            details: errorDetails,
          });
          return;
        }
      }

      // Show success state (user will click OK to redirect)
      setLoading(false);
      setSubmissionSuccess(true);
      setSuccessMessage(successMsg);

    } catch (error: any) {
      console.error('Error submitting request:', error);
      const errorMessage = getApiErrorMessage(error, 'An unexpected error occurred');
      setSubmissionError({
        stage: 'submission',
        message: errorMessage,
        details: 'Please try again or contact support if the issue persists.',
      });
      setLoading(false);
    }
  };


  // If form is shown, render the form page
  if (showForm) {
    return (
      <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 0, pb: 3 }}>
        {/* Page Header */}
        <PageHeader
          title={getFormTitle(selectedRequestType)}
          subtitle={selectedLevel ? `Level: ${getLevelLabel(selectedLevel)}` : undefined}
          actionButton={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBackToRequestSelection}
                sx={{
                  borderColor: SEARCE_COLORS.border,
                  color: SEARCE_COLORS.textSecondary,
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  borderRadius: 2,
                  '&:hover': {
                    borderColor: SEARCE_COLORS.textSecondary,
                    backgroundColor: alpha(SEARCE_COLORS.textSecondary, 0.04),
                  },
                }}
              >
                Close Form
              </Button>
              <Button
                variant="contained"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToAnalytics}
                sx={{
                  backgroundColor: SEARCE_COLORS.primary,
                  color: SEARCE_COLORS.white,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  py: 1,
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: SEARCE_COLORS.primaryDark,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  },
                }}
              >
                Back to Analytics
              </Button>
            </Box>
          }
        />

        {/* Submission Status Modal */}
        <SubmissionStatusModal
          loading={loading}
          fileUploading={fileUploading}
          slideCreating={slideCreating}
          submissionSuccess={submissionSuccess}
          successMessage={successMessage}
          submissionError={submissionError}
          selectedRequestType={selectedRequestType}
          onRetryUpload={() => {
                          setSubmissionError({ stage: null, message: '', details: '', ticketId: '' });
                          setLoading(false);
                          setFileUploading(false);
                          setSlideCreating(false);
                          handleSubmit({ preventDefault: () => {} } as React.FormEvent);
                        }}
          onClearError={() => {
                        setSubmissionError({ stage: null, message: '', details: '', ticketId: '' });
                        setLoading(false);
                        setFileUploading(false);
                        setSlideCreating(false);
          }}
          onClearFile={() => {
            setFormData(prev => ({ ...prev, closureReport: null, appreciationFile: null }));
          }}
        />

        {/* Form Content */}
        <Container maxWidth="md" sx={{ mt: 0 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${SEARCE_COLORS.border}`,
              overflow: 'hidden',
              position: 'relative',
            }}
          >

            {/* Accent bar */}
            <Box
              sx={{
                height: 4,
                background: `linear-gradient(90deg, ${SEARCE_COLORS.primary} 0%, ${SEARCE_COLORS.secondary} 100%)`,
              }}
            />

            <Box component="form" onSubmit={handleSubmit} sx={{ p: 4 }}>
              <fieldset 
                disabled={isFormDisabled} 
                style={{ 
                  border: 'none', 
                  padding: 0, 
                  margin: 0,
                  opacity: isFormDisabled ? 0.6 : 1,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: isFormDisabled ? 'none' : 'auto',
                }}
              >
              {/* Customer Feedback Form */}
              {selectedRequestType === 'feedback' && (
                <FeedbackForm
                  ref={feedbackFormRef}
                  formData={formData}
                  onInputChange={handleInputChange}
                  onFileChange={handleFileChange}
                  projectOptions={projectOptions}
                  projectsLoading={projectsLoading}
                  projectStatusFilter={projectStatusFilter}
                  onProjectStatusFilterChange={setProjectStatusFilter}
                  selectedProject={selectedProject}
                  onProjectSelect={handleProjectSelect}
                  isFormDisabled={isFormDisabled}
                />
              )}

              {/* Customer Appreciation Form */}
              {selectedRequestType === 'appreciation' && (
                <AppreciationForm
                  formData={formData}
                  onInputChange={handleInputChange}
                  selectedLevel={selectedLevel}
                  userEmail={user?.email || ''}
                  projectOptions={projectOptions}
                  projectsLoading={projectsLoading}
                  projectStatusFilter={projectStatusFilter}
                  onProjectStatusFilterChange={setProjectStatusFilter}
                  selectedProject={selectedProject}
                  onProjectSelect={handleProjectSelect}
                  teamMemberOptions={teamMemberOptions}
                  teamMembersLoading={teamMembersLoading}
                  selectedTeamMembers={selectedTeamMembers}
                  onTeamMemberSelect={handleTeamMemberSelect}
                  isFormDisabled={isFormDisabled}
                />
              )}

              {/* Customer Escalation Form */}
              {selectedRequestType === 'escalation' && (
                <EscalationForm
                  formData={formData}
                  onInputChange={handleInputChange}
                  selectedLevel={selectedLevel}
                  isEscalationResolved={isEscalationResolved}
                  onEscalationResolvedChange={(resolved) => {
                    setIsEscalationResolved(resolved);
                    if (!resolved) {
                              setFormData(prev => ({
                                ...prev,
                                dateResolved: '',
                                solutionProposed: '',
                                rootCause: '',
                                learnings: '',
                              }));
                            }
                          }}
                  projectOptions={projectOptions}
                  projectsLoading={projectsLoading}
                  projectStatusFilter={projectStatusFilter}
                  onProjectStatusFilterChange={setProjectStatusFilter}
                  selectedProject={selectedProject}
                  onProjectSelect={handleProjectSelect}
                  teamMemberOptions={teamMemberOptions}
                  teamMembersLoading={teamMembersLoading}
                  selectedTeamMembers={selectedTeamMembers}
                  onTeamMemberSelect={handleTeamMemberSelect}
                  isFormDisabled={isFormDisabled}
                />
              )}

              {/* Solution Story Form */}
              {selectedRequestType === 'solution-story' && (
                <SolutionStoryForm
                  formData={formData}
                  onInputChange={handleInputChange}
                  onFileChange={handleFileChange}
                  projectOptions={projectOptions}
                  projectsLoading={projectsLoading}
                  projectStatusFilter={projectStatusFilter}
                  onProjectStatusFilterChange={setProjectStatusFilter}
                  selectedProject={selectedProject}
                  onProjectSelect={handleProjectSelect}
                  isFormDisabled={isFormDisabled}
                />
              )}

              {/* Public Testimonial Form */}
              {selectedRequestType === 'public-testimonial' && (
                <PublicTestimonialForm
                  formData={formData}
                  onInputChange={handleInputChange}
                  projectOptions={projectOptions}
                  projectsLoading={projectsLoading}
                  projectStatusFilter={projectStatusFilter}
                  onProjectStatusFilterChange={setProjectStatusFilter}
                  selectedProject={selectedProject}
                  onProjectSelect={handleProjectSelect}
                  isFormDisabled={isFormDisabled}
                />
              )}

              </fieldset>

              {/* Action Buttons */}
              <ActionButtons
                selectedRequestType={selectedRequestType}
                isFormDisabled={isFormDisabled}
                loading={loading}
                submissionSuccess={submissionSuccess}
                onBack={handleBackToRequestSelection}
                onSaveDraft={selectedRequestType === 'solution-story' ? handleSaveSolutionStoryDraft : undefined}
                draftSaving={draftSaving}
              />
            </Box>
          </Paper>
        </Container>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // Request Type Selection Page
  return (
    <RequestTypeSelectionPage
      selectedRequestType={selectedRequestType}
      selectedLevel={selectedLevel}
      onRequestTypeSelect={handleRequestTypeSelect}
      onLevelSelect={handleLevelSelect}
      onBackToAnalytics={handleBackToAnalytics}
      onClearSelection={handleBackToRequestSelection}
    />
  );
}
